/**
 * Footer tab of Admin → Menu, Splash & Footer.
 *
 * Each field is either inherited (from the Trusscore row for a retailer, or the
 * built-in footer for Trusscore) or customized here. Turning "Customize" on
 * seeds the field with what it currently inherits, so editing starts from the
 * live text; turning it off drops the field so it inherits again.
 */

import React, { lazy, Suspense, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import { ArrowDown, ArrowUp, Loader2, Plus, Trash2 } from 'lucide-react';
import { BUILTIN_FOOTER_LINKS, SiteFooterConfig, SiteFooterLink } from '../../config/siteExperience';

const RichTextEditor = lazy(() => import('../../components/admin/RichTextEditor'));
const makeId = () => `link-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

interface Props {
  scopeKey: string;
  footer: SiteFooterConfig;
  /** What this scope inherits: the Trusscore row's fields (retailer scope) or {} (Trusscore scope). */
  inheritedFrom: SiteFooterConfig | null;
  isRetailer: boolean;
  themeStyle: React.CSSProperties;
  onChange: (footer: SiteFooterConfig) => void;
}

export const AdminSiteFooterEditor: React.FC<Props> = ({ scopeKey, footer, inheritedFrom, isRetailer, themeStyle, onChange }) => {
  // The built-in footer in English (the admin is English-only).
  const { i18n } = useTranslation();
  const en = useMemo(() => i18n.getFixedT('en', 'common'), [i18n]);
  const inherited = useMemo(() => ({
    copyright: inheritedFrom?.copyright ?? String(en('footer.copyright', { year: '{year}' })),
    links: inheritedFrom?.links ?? BUILTIN_FOOTER_LINKS.map(l => ({ id: l.id, label: String(en(`footer.${l.labelKey}`)), url: l.url, target: '_blank' as const })),
    disclaimerHtml: inheritedFrom?.disclaimerHtml ?? `<p>${String(en('footer.disclaimer'))}</p>`,
  }), [inheritedFrom, en]);
  const source = isRetailer ? 'the Trusscore default' : 'the built-in footer';

  const set = <K extends 'copyright' | 'links' | 'disclaimerHtml'>(key: K, value: SiteFooterConfig[K] | undefined) => {
    const next: SiteFooterConfig = { ...footer };
    if (value === undefined) delete next[key]; else next[key] = value;
    onChange(next);
  };
  const toggle = (key: 'copyright' | 'links' | 'disclaimerHtml', on: boolean) =>
    set(key, on ? (key === 'links' ? inherited.links.map(l => ({ ...l })) : inherited[key]) as never : undefined);

  const links = footer.links ?? [];
  const setLinks = (next: SiteFooterLink[]) => set('links', next);
  const patchLink = (idx: number, patch: Partial<SiteFooterLink>) => setLinks(links.map((l, i) => i === idx ? { ...l, ...patch } : l));
  const moveLink = (idx: number, dir: -1 | 1) => { const to = idx + dir; if (to < 0 || to >= links.length) return; const n = [...links]; [n[idx], n[to]] = [n[to], n[idx]]; setLinks(n); };

  const effective = {
    copyright: (footer.copyright ?? inherited.copyright).split('{year}').join(String(new Date().getFullYear())),
    links: footer.links ?? inherited.links,
    disclaimerHtml: DOMPurify.sanitize(footer.disclaimerHtml ?? inherited.disclaimerHtml, { ADD_ATTR: ['target'] }),
  };

  const renderSwitch = (field: 'copyright' | 'links' | 'disclaimerHtml', label: string) => (
    <div className="form-check form-switch mb-2">
      <input id={`footer-${field}`} type="checkbox" className="form-check-input" checked={footer[field] !== undefined} onChange={e => toggle(field, e.target.checked)} />
      <label htmlFor={`footer-${field}`} className="form-check-label fw-semibold small">{label}</label>
      {footer[field] === undefined && <span className="text-muted small ms-2">Inherited from {source}</span>}
    </div>
  );

  return (
    <div className="row g-4">
      <div className="col-xl-7">
        <p className="text-muted small">
          {isRetailer
            ? 'Customize only what this retailer needs; everything else keeps following the Trusscore default.'
            : 'The Trusscore default every retailer inherits. Left un-customized, the built-in footer is shown (translated).'}
        </p>

        <div className="border rounded-3 p-3 mb-3">
          {renderSwitch('copyright', 'Copyright line')}
          {footer.copyright !== undefined
            ? <><input className="form-control form-control-sm" value={footer.copyright} onChange={e => set('copyright', e.target.value)} /><div className="form-text">{'{year}'} becomes the current year. Leave empty to show no copyright line.</div></>
            : <div className="small text-muted">{inherited.copyright}</div>}
        </div>

        <div className="border rounded-3 p-3 mb-3">
          {renderSwitch('links', 'Links')}
          {footer.links !== undefined ? (
            <>
              {links.length === 0 && <div className="small text-muted mb-2">No links will be shown.</div>}
              {links.map((link, idx) => (
                <div key={link.id} className="d-flex align-items-end gap-2 mb-2">
                  <div style={{ flex: '0 0 30%' }}><label className="form-label small mb-1">Label</label><input className="form-control form-control-sm" value={link.label} onChange={e => patchLink(idx, { label: e.target.value })} /></div>
                  <div className="flex-grow-1"><label className="form-label small mb-1">URL</label><input className="form-control form-control-sm" value={link.url} onChange={e => patchLink(idx, { url: e.target.value })} /></div>
                  <div style={{ flex: '0 0 90px' }}><label className="form-label small mb-1">Opens</label><select className="form-select form-select-sm" value={link.target} onChange={e => patchLink(idx, { target: e.target.value as '_self' | '_blank' })}><option value="_blank">New tab</option><option value="_self">Same tab</option></select></div>
                  <div className="btn-group btn-group-sm">
                    <button className="btn btn-outline-secondary" onClick={() => moveLink(idx, -1)} disabled={idx === 0}><ArrowUp size={12} /></button>
                    <button className="btn btn-outline-secondary" onClick={() => moveLink(idx, 1)} disabled={idx === links.length - 1}><ArrowDown size={12} /></button>
                    <button className="btn btn-outline-danger" onClick={() => setLinks(links.filter((_, i) => i !== idx))}><Trash2 size={12} /></button>
                  </div>
                </div>
              ))}
              <button className="btn btn-outline-primary btn-sm" onClick={() => setLinks([...links, { id: makeId(), label: 'New link', url: 'https://', target: '_blank' }])} disabled={links.length >= 12}><Plus size={13} /> Link</button>
            </>
          ) : (
            <div className="small text-muted">{inherited.links.length ? inherited.links.map(l => l.label).join(' · ') : 'No links'}</div>
          )}
        </div>

        <div className="border rounded-3 p-3">
          {renderSwitch('disclaimerHtml', 'Disclaimer / legal text')}
          {footer.disclaimerHtml !== undefined ? (
            <Suspense fallback={<div className="p-4"><Loader2 className="animate-spin" /></div>}>
              <RichTextEditor key={`footer-${scopeKey}`} value={footer.disclaimerHtml} onChange={html => set('disclaimerHtml', html)} />
            </Suspense>
          ) : (
            <div className="small text-muted" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(inherited.disclaimerHtml) }} />
          )}
        </div>
        <div className="form-text">Labels, copyright and disclaimer are translated through Admin → Translations, like the menu and splash.</div>
      </div>

      <div className="col-xl-5">
        <h6 className="fw-bold">Preview</h6>
        <div className="rounded overflow-hidden" style={{ ...themeStyle, background: 'var(--partner-footer-bg, #1f2937)', color: 'var(--partner-footer-fg, #d1d5db)', padding: '14px 16px', fontSize: 14 }}>
          <div className="d-flex flex-wrap justify-content-between gap-2 pb-2" style={{ borderBottom: '1px solid var(--partner-footer-muted-fg, #374151)' }}>
            <span>{effective.copyright}</span>
            <span className="d-flex flex-wrap gap-3">{effective.links.map(l => <span key={l.id} style={{ opacity: 0.8 }}>{l.label}</span>)}</span>
          </div>
          <div className="text-center pt-2" style={{ color: 'var(--partner-footer-muted-fg, #6b7280)', fontSize: '0.75em' }} dangerouslySetInnerHTML={{ __html: effective.disclaimerHtml }} />
        </div>
      </div>
    </div>
  );
};

export default AdminSiteFooterEditor;
